// Lightweight CSS-only confetti burst — fires once per `triggerKey`
// change. No deps, no canvas — 18 absolutely-positioned chips that
// fade + drift for ~1s, then unmount.
import { useEffect, useMemo, useState } from "react";

interface Props {
  triggerKey: number;
}

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--primary) / 0.7)",
  "hsl(var(--accent))",
  "hsl(var(--foreground))",
];

const SkillCompleteCelebration = ({ triggerKey }: Props) => {
  const [visible, setVisible] = useState(false);

  // Stable per-burst random seeds so chips don't reshuffle mid-anim.
  const chips = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 120,
        dx: (Math.random() - 0.5) * 240,
        rot: Math.random() * 540 - 270,
        color: COLORS[i % COLORS.length],
        size: 6 + Math.random() * 6,
      })),
    // intentionally re-seeded each trigger
    [triggerKey],
  );

  useEffect(() => {
    if (!triggerKey) return;
    setVisible(true);
    const t = window.setTimeout(() => setVisible(false), 1100);
    return () => window.clearTimeout(t);
  }, [triggerKey]);

  if (!visible) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[200] overflow-hidden"
    >
      <style>{`
        @keyframes hl-confetti-drop {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate3d(var(--dx), 90vh, 0) rotate(var(--rot)); opacity: 0; }
        }
      `}</style>
      {chips.map((c) => (
        <span
          key={c.id}
          className="absolute top-0 rounded-sm"
          style={{
            left: `${c.left}%`,
            width: c.size,
            height: c.size,
            background: c.color,
            ["--dx" as string]: `${c.dx}px`,
            ["--rot" as string]: `${c.rot}deg`,
            animation: `hl-confetti-drop 1s cubic-bezier(.2,.8,.4,1) ${c.delay}ms forwards`,
          }}
        />
      ))}
    </div>
  );
};

export default SkillCompleteCelebration;
